/**
 * C5 §6 — persisted cart line validation (shared read paths; no cartStorage import).
 */

import type { ShopCart, ShopCartLine, ShopCartLineProduct, ShopCartLineVariant } from '../types.js';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isValidShopCartLineProduct(value: unknown): value is ShopCartLineProduct {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const product = value as Record<string, unknown>;
  if (!isFiniteNumber(product.id) || product.id <= 0) {
    return false;
  }
  if (typeof product.name !== 'string') {
    return false;
  }
  if (!isFiniteNumber(product.price) || product.price < 0) {
    return false;
  }
  if (
    'nameLocales' in product &&
    product.nameLocales !== null &&
    product.nameLocales !== undefined &&
    typeof product.nameLocales !== 'object'
  ) {
    return false;
  }
  if (
    'imageUrl' in product &&
    product.imageUrl !== null &&
    product.imageUrl !== undefined &&
    typeof product.imageUrl !== 'string'
  ) {
    return false;
  }
  if (
    'description' in product &&
    product.description !== null &&
    product.description !== undefined &&
    typeof product.description !== 'string'
  ) {
    return false;
  }
  return true;
}

function isValidShopCartLineVariant(value: unknown): value is ShopCartLineVariant | null {
  if (value === null) {
    return true;
  }
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const variant = value as Record<string, unknown>;
  if (!isFiniteNumber(variant.id) || variant.id <= 0) {
    return false;
  }
  if (variant.name !== null && typeof variant.name !== 'string') {
    return false;
  }
  if (!isFiniteNumber(variant.price) || variant.price < 0) {
    return false;
  }
  if (variant.sku !== null && typeof variant.sku !== 'string') {
    return false;
  }
  if (variant.barcode !== null && typeof variant.barcode !== 'string') {
    return false;
  }
  return true;
}

/** Type-guard for a single persisted cart line (C5 §6). */
export function isValidPersistedCartLine(line: unknown): line is ShopCartLine {
  if (typeof line !== 'object' || line === null) {
    return false;
  }
  const record = line as Record<string, unknown>;
  if (typeof record.key !== 'string' || record.key.length === 0) {
    return false;
  }
  if (!isValidShopCartLineProduct(record.product)) {
    return false;
  }
  if (!isValidShopCartLineVariant(record.variant)) {
    return false;
  }
  if (
    !isFiniteNumber(record.quantity) ||
    !Number.isInteger(record.quantity) ||
    record.quantity <= 0
  ) {
    return false;
  }
  if (!isFiniteNumber(record.unitPrice) || record.unitPrice < 0) {
    return false;
  }
  return true;
}

/** Returns validated lines, or `null` when any line is malformed. */
export function validatePersistedCartLines(lines: unknown): ShopCart['lines'] | null {
  if (!Array.isArray(lines)) {
    return null;
  }
  const validated: ShopCart['lines'] = [];
  for (const line of lines) {
    if (!isValidPersistedCartLine(line)) {
      return null;
    }
    validated.push(line);
  }
  return validated;
}
